import { DataManager } from "../model/DataManager.js";
import { VOVideo } from "../model/DataStructure.js";
import { Language } from "../model/Language.js";
import { PlayButton } from "./PlayButton.js";
import { VideoSubtitle } from "./VideoSubtitle.js";

export class VideoPlayer {

    public onEnded = e5.Signal.create<[VOVideo]>();

    public onNext = e5.Signal.create<[VOVideo]>();
    public onPrev = e5.Signal.create<[VOVideo]>();

    public element: HTMLDivElement;

    private _videoElement: HTMLVideoElement;
    private _video: VOVideo;
    private _url: string = "";
    private _thumb: HTMLDivElement;
    private _seek: HTMLDivElement;
    private _isDown: boolean = false;
    private _toggle: HTMLDivElement;
    private _full: HTMLDivElement;
    private _next: HTMLDivElement;
    private _prev: HTMLDivElement;

    private _poster: HTMLDivElement;
    private _playButton: PlayButton;
    private _videoSubtitle: VideoSubtitle;
    private _subtitleElement: HTMLDivElement;
    private _subtitleButton: HTMLDivElement;
    private _subtitlesEnabled: boolean = true;

    constructor(private _data: DataManager) {
        this.element = document.createElement("div");
        this.element.className = "video-player";
        this.element.innerHTML = `
        <video class="video-player-video" width="100%" height="100%"></video>
        <div class="video-player-control">
            <div class="video-player-button toggle">
                <div class="video-player-button-icon deactive icon-420"></div>
                <div class="video-player-button-icon active icon-421"></div>
            </div>
            <div class="video-player-seek">
                <div class="video-player-seek-background"></div>
                <div class="video-player-seek-track"></div>
                <div class="video-player-seek-thumb">
                    <div class="video-player-seek-thumb-inner"></div>
                </div>
            </div>
            <div class="video-player-button prev">
                <div class="video-player-button-icon icon-424"></div>
            </div>
            <div class="video-player-button next">
                <div class="video-player-button-icon icon-426"></div>
            </div>
            <div class="video-player-button state subtitle active">
                <div class="video-player-button-icon icon-654"></div>
            </div>
            <div class="video-player-button full">
                <div class="video-player-button-icon deactive icon-756"></div>
                <div class="video-player-button-icon active icon-757"></div>
            </div>
        </div>
        <div class="video-player-subtitle"></div>
        <div class="video-player-poster"></div>
        <div class="video-player-fullscreen-button">
            <div class="video-player-button-icon deactive icon-756"></div>
            <div class="video-player-button-icon active icon-757"></div>
        </div>
        `;

        this._videoElement = this.element.querySelector(".video-player-video");
        this._poster = this.element.querySelector(".video-player-poster");
        this._thumb = this.element.querySelector(".video-player-seek-thumb");
        this._seek = this.element.querySelector(".video-player-seek");
        this._toggle = this.element.querySelector(".video-player-button.toggle");
        this._full = this.element.querySelector(".video-player-button.full");
        this._next = this.element.querySelector(".video-player-button.next");
        this._prev = this.element.querySelector(".video-player-button.prev");
        this._subtitleButton = this.element.querySelector(".video-player-button.subtitle");
        this._subtitleElement = this.element.querySelector(".video-player-subtitle");

        var fullscreenButton = this.element.querySelector(".video-player-fullscreen-button");
        fullscreenButton.classList.toggle("hidden", !this._data.config.show_fullscreen_buttons
            || this._data.config.fullscreen_button_positions.indexOf("overlay") === -1);
        fullscreenButton.addEventListener("click", () => {
            document.body.classList.toggle("fullscreen");
        });

        this._full.classList.toggle("hidden", !this._data.config.show_fullscreen_buttons
            || this._data.config.fullscreen_button_positions.indexOf("video_control") === -1);

        this._playButton = new PlayButton();
        this._poster.appendChild(this._playButton.element);

        this._videoSubtitle = new VideoSubtitle();
        this._subtitleElement.appendChild(this._videoSubtitle.element);
        this._videoSubtitle.bind(this._videoElement);

        // this.controls = true;
        if (this._data.config.video_end_bahaviour == "loop") {
            this.loop = true;
        }

        this.subtitlesEnabled = this._data.config.video_subtitles;

        this._videoElement.addEventListener("timeupdate", () => {
            this.handleTimeUpdate();
        });

        var actor = new e5.Actor(this._seek);
        actor.maxPointers = 1;

        var move = () => {
            var local = e5.Transform.toLocal(this._seek, actor.pointer.x, actor.pointer.y);
            var ratio = e5.Calc.clamp(local.x, 0, this._seek.offsetWidth) / this._seek.offsetWidth;
            this._thumb.style.left = (ratio * 100).toPrecision(4) + "%";
        };
        actor.onDown.add(() => {
            this._isDown = true;
            move();
        });

        actor.onMove.add(() => {
            move();
        });

        actor.onClear.add(() => {
            move();

            var local = e5.Transform.toLocal(this._seek, actor.pointer.x, actor.pointer.y);
            var ratio = e5.Calc.clamp(local.x, 0, this._seek.offsetWidth) / this._seek.offsetWidth;
            this._videoElement.currentTime = ratio * this._videoElement.duration;

            this._isDown = false;
        });

        this._videoElement.addEventListener("playing", () => {
            this._toggle.classList.add("active");
            this._poster.classList.remove("active");
            this.element.classList.remove("preloading");
        });

        this._videoElement.addEventListener("pause", () => {
            this._toggle.classList.remove("active");
        });

        this._toggle.addEventListener("click", () => {
            if (this._videoElement.paused) {
                this.play();
            } else {
                this.pause();
            }
        });

        this._full.addEventListener("click", () => {
            // this._full.classList.toggle("active");
            // var active = this._full.classList.contains("active");
            document.body.classList.toggle("fullscreen");
        });

        this._videoElement.addEventListener("ended", () => {
            this.onEnded.dispatch(this._video);
        });

        this._next.addEventListener("click", () => {
            this.onNext.dispatch(this._video);
        });

        this._prev.addEventListener("click", () => {
            this.onPrev.dispatch(this._video);
        });

        this._subtitleButton.addEventListener("click", () => {
            this.subtitlesEnabled = !this.subtitlesEnabled;
        });

        this._playButton.onStart.add(() => {
            this._poster.classList.add("active");
            this.element.classList.add("preloading");
        });

        this._playButton.onComplete.add(() => {
            if (this._data.config.fullscreen_mode == "auto") {
                document.body.classList.add("fullscreen");
            }
            this._poster.classList.remove("active");
            this.element.classList.remove("preloading");
            this.updateSource();
            this.play();
        });

        Language.onChange.add(() => {
            this.updatePoster();
            this.updateSource();
        });
    }

    private get subtitlesEnabled(): boolean {
        return this._subtitlesEnabled;
    }

    private set subtitlesEnabled(value: boolean) {
        if (this._subtitlesEnabled == value) {
            return;
        }
        this._subtitlesEnabled = value;
        this._subtitleButton.classList.toggle("active", value);
        this._subtitleElement.classList.toggle("hidden", !value);
    }

    private handleTimeUpdate(): void {
        if (!this._isDown) {
            this._thumb.style.left = ((this._videoElement.currentTime / this._videoElement.duration) * 100).toPrecision(4) + "%";
        }
    }

    public get loop(): boolean {
        return this._videoElement.loop;
    }

    public set loop(value: boolean) {
        this._videoElement.loop = value;
    }

    public get controls(): boolean {
        return this._videoElement.hasAttribute("controls");
    }

    public set controls(value: boolean) {
        if (value) {
            this._videoElement.setAttribute("controls", "");
        } else {
            this._videoElement.removeAttribute("controls");
        }
    }

    public pause(): void {
        this._videoElement.pause();
        this._playButton.clear();
    }

    public stop(): void {
        this._videoElement.pause();
        this._videoElement.removeAttribute("src");
    }

    public reset(): void {
        this.subtitlesEnabled = this._data.config.video_subtitles;
    }

    public get video(): VOVideo {
        return this._video;
    }

    public set video(video: VOVideo) {
        if (!video || this._video == video) {
            if (video && this._video == video) {
                this.play();
            }
            return;
        }
        this._video = video;

        this.pause();
        // this._thumb.style.left = "0";

        this._playButton.start();

        this.updatePoster();
    }

    private updateSubtitle(): void {
        var subtitleUrl = this.getSubtitleUrl();
        this._subtitleButton.classList.toggle("hidden", !subtitleUrl);
        this._videoSubtitle.load(subtitleUrl);
    }

    private getSubtitleUrl(): string {
        var subtitleUrl = null;
        if (!this._video) {
            subtitleUrl = null;
        }
        else if (Language.current == "de") {
            if (this._video.video_subtitle_de) {
                subtitleUrl = this._video.video_subtitle_de;
            }
        } else if (Language.current == "en") {
            if (this._video.video_subtitle_en) {
                subtitleUrl = this._video.video_subtitle_en;
            }
        }
        return subtitleUrl;
    }

    private updateSource(): void {
        this.updateSubtitle();

        if (!this._video) {
            return;
        }

        var media = Language.trans(this._video.video_de, this._video.video_en);
        if (this._url == media.url) {
            return;
        }

        this._thumb.style.left = "0";
        var playing = !this._videoElement.paused;
        this._url = media.url;
        this._videoElement.src = this._url;

        if (playing) {
            this.play();
        }
    }

    public play(): void {
        var p = this._videoElement.play();
        if (p) {
            p.catch((e) => {
                var isMuted = this._videoElement.muted;
                this._videoElement.muted;
                if (!isMuted) {
                    this._videoElement.muted = true;
                    this._videoElement.play();

                    $(document).one("mousedown", () => {
                        this._videoElement.muted = false;
                    });
                }
            });
        }
    }

    private updatePoster(): void {
        if (!this._video) {
            this._poster.style.backgroundImage = "";
            return;
        }
        this._poster.style.backgroundImage = `url(${Language.trans(this._video.preview_de, this._video.preview_en)})`;
    }
}